import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, Phone, MapPin } from "lucide-react";
import { COMPANY, DIVISIONS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", division: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Vul uw naam, e-mail en bericht in.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote-requests`, form);
      setDone(true);
      toast.success("Bedankt! Uw aanvraag is verstuurd. We nemen snel contact op.");
      setForm({ name: "", email: "", phone: "", division: "", message: "" });
    } catch (err) {
      toast.error("Er ging iets mis. Probeer het later opnieuw of bel ons.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-black/20 py-3 text-base outline-none focus:border-[var(--turq)] transition-colors duration-300 placeholder:text-black/30";

  return (
    <section id="contact" data-testid="contact-section" className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40">
      <div className="grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-[var(--orange)]" />
            <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-black/50">Contact</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tight leading-[0.98]">
            Klaar voor de<br /><span className="text-[var(--orange)]">volgende stap?</span>
          </h2>
          <p className="mt-8 text-lg text-black/70 leading-relaxed max-w-md">
            Vraag vrijblijvend een offerte aan of stel uw vraag. We reageren doorgaans binnen één werkdag.
          </p>

          <div className="mt-12 space-y-6">
            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="h-11 w-11 flex items-center justify-center border border-black/15 group-hover:bg-[var(--ink)] group-hover:text-white transition-colors duration-300">
                <Mail size={18} />
              </span>
              <span className="text-lg group-hover:text-[var(--turq)] transition-colors">{COMPANY.email}</span>
            </a>
            <a href={COMPANY.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="h-11 w-11 flex items-center justify-center border border-black/15 group-hover:bg-[var(--ink)] group-hover:text-white transition-colors duration-300">
                <Phone size={18} />
              </span>
              <span className="text-lg group-hover:text-[var(--turq)] transition-colors">{COMPANY.phone}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="h-11 w-11 flex items-center justify-center border border-black/15">
                <MapPin size={18} />
              </span>
              <span className="text-lg">{COMPANY.address}</span>
            </div>
          </div>
        </div>

        <div className="md:col-span-7 md:pl-8">
          <div className="border border-black/10 p-8 md:p-12 bg-white">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="py-16 text-center"
                data-testid="contact-success"
              >
                <div className="font-display text-3xl font-medium tracking-tight">Aanvraag ontvangen ✦</div>
                <p className="mt-4 text-black/60">Bedankt voor uw bericht. We nemen zo snel mogelijk contact met u op.</p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="contact-reset"
                  className="mt-8 font-mono-tech text-xs uppercase tracking-[0.15em] underline underline-offset-4"
                >
                  Nieuwe aanvraag
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-black/50">Naam *</label>
                    <input data-testid="input-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="Uw naam" />
                  </div>
                  <div>
                    <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-black/50">E-mail *</label>
                    <input data-testid="input-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="naam@voorbeeld.nl" />
                  </div>
                  <div>
                    <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-black/50">Telefoon</label>
                    <input data-testid="input-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="+31 6 ..." />
                  </div>
                  <div>
                    <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-black/50">Afdeling</label>
                    <select data-testid="input-division" value={form.division} onChange={update("division")} className={`${inputCls} cursor-pointer`}>
                      <option value="">Kies een afdeling</option>
                      {DIVISIONS.map((d) => (
                        <option key={d.id} value={d.title}>{d.title}</option>
                      ))}
                      <option value="Anders / algemeen">Anders / algemeen</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-black/50">Uw project / vraag *</label>
                  <textarea data-testid="input-message" value={form.message} onChange={update("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Vertel ons kort over uw project..." />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-quote"
                  className="inline-flex items-center gap-2 bg-[var(--ink)] text-white px-8 py-4 text-sm font-medium hover:bg-[var(--orange)] hover:-translate-y-0.5 transition-[background-color,transform] duration-300 disabled:opacity-60"
                >
                  {loading ? <><Loader2 size={18} className="animate-spin" /> Versturen...</> : <>Offerte aanvragen <ArrowUpRight size={18} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
